import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useTokens } from '@vibesites/runtime/react';
import { contrastRatio } from '../utils/luminance';

/**
 * Shared building blocks for the Gallery sections (apartment grid, community
 * grid, and the central separator). Not a registered component — has no
 * `contentSchema`, never appears in `registry.ts`, and is not re-exported
 * from `components/index.ts`.
 *
 * The schema linter skips files without a contentSchema, so this helper
 * sits safely alongside the registered gallery sections without tripping
 * any rules (every photo is rendered through VibesitesImage).
 */

/**
 * Resolved text color for the gallery row heading on top of the white gallery
 * background. Default is the theme's primary color, but if the primary is too
 * light to read against white we fall back to black. The 3:1 threshold matches
 * WCAG AA for large text — at 2.75rem / 600 weight the headings qualify, and
 * anything washing out below that (pale gold, ivory, mint, etc.) becomes
 * black so the section heading stays legible without per-theme overrides.
 *
 * Returned as a plain color string so callers can apply it via an inline
 * `style={{ color }}` override on the heading element; that wins over the
 * `color: var(--vibe-color-primary)` rule in `gallery-row__heading`.
 */
export function useGalleryHeadingColor(): string {
  const tokens = useTokens();
  return useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return 'var(--vibe-color-primary)';
    return contrastRatio(primary, '#ffffff') < 3 ? '#000000' : primary;
  }, [tokens]);
}

export interface GalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  description?: string;
  source_page_urls?: string;
  original_url?: string;
}

interface MobileGalleryCarouselProps {
  images: GalleryImage[];
  onSelect: (index: number) => void;
}

export function MobileGalleryCarousel({ images, onSelect }: MobileGalleryCarouselProps) {
  const [current, setCurrent] = useState(0);
  const count = images.length;
  const touchStartX = useRef<number | null>(null);

  const advance = useCallback(
    (dir: 1 | -1) => {
      if (count === 0) return;
      setCurrent((p) => (p + dir + count) % count);
    },
    [count],
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      touchStartX.current = null;
      if (Math.abs(dx) < 40) return;
      if (dx < 0) advance(1);
      else advance(-1);
    },
    [advance],
  );

  if (count === 0) return null;

  // Clamp current in case the images list changes length.
  const safeCurrent = Math.min(current, count - 1);

  return (
    <div
      className="gallery-mobile-carousel"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/*
        All slides are rendered stacked so they decode up front and we never
        see the carousel's black background between transitions. Only the
        active slide is opaque (and is the only one that receives clicks).
      */}
      {images.map((img, i) => (
        <div
          key={img.image || i}
          className={`gallery-mobile-carousel__slide gallery-item${i === safeCurrent ? ' is-active' : ''}`}
          aria-hidden={i === safeCurrent ? undefined : true}
          onClick={i === safeCurrent ? () => onSelect(safeCurrent) : undefined}
        >
          <VibesitesImage
            url={img.image}
            alt={img.alt_text || img.title || ''}
            className="w-full h-full object-cover"
            priority
          />
        </div>
      ))}

      {count > 1 && (
        <>
          <button
            type="button"
            className="gallery-mobile-carousel__arrow gallery-mobile-carousel__arrow--prev"
            onClick={(e) => {
              e.stopPropagation();
              advance(-1);
            }}
            aria-label="Previous image"
          >
            <ChevronLeft aria-hidden="true" size={22} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            className="gallery-mobile-carousel__arrow gallery-mobile-carousel__arrow--next"
            onClick={(e) => {
              e.stopPropagation();
              advance(1);
            }}
            aria-label="Next image"
          >
            <ChevronRight aria-hidden="true" size={22} strokeWidth={2.5} />
          </button>

          <div className="gallery-mobile-carousel__dots" aria-hidden="true">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`gallery-mobile-carousel__dot${i === safeCurrent ? ' is-active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrent(i);
                }}
                aria-label={`Show image ${i + 1}`}
              />
            ))}
          </div>

          <div className="gallery-mobile-carousel__counter" aria-live="polite">
            {safeCurrent + 1} / {count}
          </div>
        </>
      )}
    </div>
  );
}

interface GalleryRowProps {
  heading: React.ReactNode;
  images: GalleryImage[];
  onSelect: (index: number) => void;
}

export function GalleryRow({ heading, images, onSelect }: GalleryRowProps) {
  if (images.length === 0) return null;
  return (
    <div className="gallery-row">
      {heading}

      {/* Tablet / desktop: multi-column grid */}
      <div className="gallery-row__grid hidden sm:grid grid-cols-2 md:grid-cols-3 gap-3">
        {images.map((img, index) => (
          <div
            key={index}
            className="aspect-video overflow-hidden group cursor-pointer gallery-item"
            onClick={() => onSelect(index)}
          >
            <VibesitesImage
              url={img.image}
              alt={img.alt_text || img.title || ''}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
          </div>
        ))}
      </div>

      {/* Mobile: single-image swipeable carousel */}
      <div className="sm:hidden">
        <MobileGalleryCarousel images={images} onSelect={onSelect} />
      </div>
    </div>
  );
}

interface GalleryLightboxProps {
  images: GalleryImage[];
  selectedIndex: number | null;
  onClose: () => void;
  onChange: (index: number | null) => void;
}

export function GalleryLightbox({
  images,
  selectedIndex,
  onClose,
  onChange,
}: GalleryLightboxProps) {
  const total = images.length;

  const goNext = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(selectedIndex === null ? null : (selectedIndex + 1) % total);
    },
    [onChange, selectedIndex, total],
  );

  const goPrev = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(selectedIndex === null ? null : (selectedIndex - 1 + total) % total);
    },
    [onChange, selectedIndex, total],
  );

  // Keyboard navigation
  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedIndex, goNext, goPrev, onClose]);

  // Touch / swipe state. `didSwipe` lets the click-to-close handler ignore
  // the synthetic click that fires at the end of a swipe gesture.
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const didSwipe = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    didSwipe.current = false;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      const dy =
        touchStartY.current === null ? 0 : e.changedTouches[0].clientY - touchStartY.current;
      touchStartX.current = null;
      touchStartY.current = null;
      // Require mostly-horizontal movement so vertical scroll attempts don't paginate.
      if (Math.abs(dx) < 30 || Math.abs(dx) < Math.abs(dy)) return;
      didSwipe.current = true;
      if (dx < 0) goNext();
      else goPrev();
    },
    [goNext, goPrev],
  );

  // Swallow the trailing click that some browsers synthesize after a swipe,
  // so the lightbox doesn't close on swipe-release.
  const handleBackdropClick = useCallback(() => {
    if (didSwipe.current) {
      didSwipe.current = false;
      return;
    }
    onClose();
  }, [onClose]);

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null;
  if (!selectedImage) return null;

  return (
    <div
      className="gallery-lightbox"
      onClick={handleBackdropClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label="Image lightbox"
    >
      {/* Close */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="gallery-lightbox__close"
        aria-label="Close lightbox"
      >
        &times;
      </button>

      {/* Prev arrow */}
      {total > 1 && (
        <button
          onClick={goPrev}
          className="gallery-lightbox__arrow gallery-lightbox__arrow--prev"
          aria-label="Previous image"
        >
          <ChevronLeft aria-hidden="true" size={36} strokeWidth={2.25} />
        </button>
      )}

      {/* Image — also listen for swipes here so gestures starting on the image work */}
      <div
        className="gallery-lightbox__img-wrap"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <VibesitesImage
          url={selectedImage.image}
          alt={selectedImage.alt_text || selectedImage.title || ''}
          className="max-w-full max-h-[90vh] object-contain gallery-lightbox__img"
        />
      </div>

      {/* Next arrow */}
      {total > 1 && (
        <button
          onClick={goNext}
          className="gallery-lightbox__arrow gallery-lightbox__arrow--next"
          aria-label="Next image"
        >
          <ChevronRight aria-hidden="true" size={36} strokeWidth={2.25} />
        </button>
      )}

      {/* Counter */}
      {total > 1 && (
        <div className="gallery-lightbox__counter" aria-live="polite">
          {(selectedIndex ?? 0) + 1} / {total}
        </div>
      )}
    </div>
  );
}

/**
 * Shared CSS for the gallery row, mobile carousel, and lightbox. Each
 * gallery section renders this once; duplicate `<style>` blocks across
 * multiple sections are fine — the rules are identical and the browser
 * deduplicates.
 */
export function GallerySharedStyles() {
  return (
    <style>{`
      .gallery-constrained {
        /* Fill the content region (which is already offset by the fixed
           sidebar via .vibesites-layout padding) rather than a viewport-width
           value. Using vw units ignored the sidebar offset and pushed the
           gallery wider than the available space, causing horizontal scroll. */
        box-sizing: border-box;
        width: 100%;
        max-width: 100%;
        margin-left: auto;
        margin-right: auto;
        padding-left: 1.25rem;
        padding-right: 1.25rem;
      }

      .gallery-row__heading {
        font-family: var(--vibe-font-heading);
        font-size: 2.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--vibe-color-primary);
        margin-bottom: 1rem;
        text-align: center;
      }

      .gallery-item {
        animation: gallery-fade-in 0.35s ease both;
      }

      @keyframes gallery-fade-in {
        from { opacity: 0; transform: scale(0.97); }
        to { opacity: 1; transform: scale(1); }
      }

      @media (prefers-reduced-motion: reduce) {
        .gallery-item { animation: none; }
      }

      /* ── Mobile single-image carousel ─────────────────────── */
      .gallery-mobile-carousel {
        position: relative;
        width: 100%;
        aspect-ratio: 16 / 9;
        overflow: hidden;
        background: #000;
        border-radius: 3px;
        touch-action: pan-y;
        -webkit-user-select: none;
        user-select: none;
      }

      .gallery-mobile-carousel__slide {
        position: absolute;
        inset: 0;
        cursor: pointer;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.25s ease;
      }
      .gallery-mobile-carousel__slide.is-active {
        opacity: 1;
        pointer-events: auto;
        z-index: 1;
      }

      .gallery-mobile-carousel__arrow {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: none;
        background: rgba(0, 0, 0, 0.45);
        color: #fff;
        font-size: 1.5rem;
        line-height: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        z-index: 2;
        padding: 0;
        transition: background 0.2s;
      }
      .gallery-mobile-carousel__arrow:active {
        background: rgba(0, 0, 0, 0.7);
      }
      .gallery-mobile-carousel__arrow--prev { left: 0.5rem; }
      .gallery-mobile-carousel__arrow--next { right: 0.5rem; }

      .gallery-mobile-carousel__dots {
        position: absolute;
        bottom: 0.6rem;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: 6px;
        z-index: 2;
        max-width: calc(100% - 6rem);
        flex-wrap: wrap;
        justify-content: center;
      }

      .gallery-mobile-carousel__dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        border: 1.5px solid rgba(255, 255, 255, 0.7);
        background: transparent;
        padding: 0;
        cursor: pointer;
        transition: background 0.2s, border-color 0.2s, transform 0.2s;
      }
      .gallery-mobile-carousel__dot.is-active {
        background: #fff;
        border-color: #fff;
      }

      .gallery-mobile-carousel__counter {
        position: absolute;
        top: 0.5rem;
        right: 0.6rem;
        font-family: var(--vibe-font-body);
        font-size: 0.75rem;
        letter-spacing: 0.04em;
        color: #fff;
        background: rgba(0, 0, 0, 0.45);
        padding: 0.15rem 0.5rem;
        border-radius: 999px;
        z-index: 2;
        pointer-events: none;
      }

      /* ── Lightbox ───────────────────────────────────────────── */
      .gallery-lightbox {
        position: fixed;
        inset: 0;
        z-index: 50;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.85);
        padding: 1rem;
        touch-action: pan-y;
      }

      /* Desktop: the sidebar header (--rise-sidebar-width) is always visible
         and sits above the lightbox (z-60). Offset the lightbox to the right
         of it so it fits the visible space instead of hiding underneath.
         Mobile/tablet show the sidebar only as an overlay sheet, so the
         lightbox stays full-width there. */
      @media (min-width: 1024px) {
        .gallery-lightbox {
          left: var(--rise-sidebar-width, 300px);
        }
      }

      .gallery-lightbox__img-wrap {
        display: flex;
        align-items: center;
        justify-content: center;
        max-width: calc(100vw - 8rem);
        max-height: 90vh;
      }
      @media (min-width: 1024px) {
        .gallery-lightbox__img-wrap {
          max-width: calc(100vw - var(--rise-sidebar-width, 300px) - 8rem);
        }
      }

      .gallery-lightbox__close {
        position: absolute;
        top: 1rem;
        right: 1rem;
        color: #fff;
        font-size: 2.25rem;
        line-height: 1;
        background: none;
        border: none;
        cursor: pointer;
        opacity: 0.85;
        z-index: 10;
        padding: 0.25rem 0.5rem;
        transition: opacity 0.15s;
      }
      .gallery-lightbox__close:hover { opacity: 1; }

      .gallery-lightbox__arrow {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 4.5rem;
        height: 4.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 4rem;
        line-height: 1;
        background: rgba(0, 0, 0, 0.35);
        border: none;
        cursor: pointer;
        padding: 0;
        border-radius: 4px;
        opacity: 0.8;
        z-index: 10;
        transition: opacity 0.15s, background 0.15s;
        -webkit-user-select: none;
        user-select: none;
      }
      .gallery-lightbox__arrow:hover {
        opacity: 1;
        background: rgba(0, 0, 0, 0.6);
      }
      .gallery-lightbox__arrow--prev { left: 0.75rem; }
      .gallery-lightbox__arrow--next { right: 0.75rem; }

      /* The image must not eat the swipe gesture (iOS Safari drag-to-save). */
      .gallery-lightbox__img {
        pointer-events: none;
        -webkit-user-drag: none;
        user-select: none;
      }
      .gallery-lightbox__img-wrap {
        touch-action: pan-y;
      }

      .gallery-lightbox__counter {
        position: absolute;
        bottom: 1.25rem;
        left: 50%;
        transform: translateX(-50%);
        color: rgba(255,255,255,0.75);
        font-size: 0.875rem;
        letter-spacing: 0.05em;
        pointer-events: none;
      }

      @media (max-width: 639px) {
        /* Mobile: gallery + heading fill the content width with a small gutter.
           width:100% (not 100vw) keeps it inside the viewport so it never
           triggers horizontal scrolling. */
        .gallery-constrained {
          width: 100%;
          max-width: 100%;
          margin-left: 0;
          margin-right: 0;
          padding-left: 0.75rem;
          padding-right: 0.75rem;
        }
        /* Carousel itself runs edge-to-edge with no rounded corners */
        .gallery-mobile-carousel {
          border-radius: 0;
        }
        /* The grid is hidden on mobile, but if it ever shows, kill the gap too */
        .gallery-row__grid {
          gap: 0;
        }

        .gallery-lightbox__img-wrap {
          max-width: 100vw;
        }
        .gallery-lightbox__arrow {
          width: 3rem;
          height: 3rem;
        }
        .gallery-lightbox__arrow--prev { left: 0.25rem; }
        .gallery-lightbox__arrow--next { right: 0.25rem; }
      }
    `}</style>
  );
}
